"use client";

import type { PreOrder, PreOrderMetadata } from "@/app/api/types/preOrder";
import { mergeMessageIdsIntoMetadata } from "./merge-message-ids";

/**
 * Convert a V2 API pre-order row (camelCase, partial-partner) into the legacy
 * snake_case PreOrder shape consumed by the rest of the frontend (LinkedPreOrdersBar,
 * PreOrderBadge, recap, etc.).
 *
 * Partner is emitted as a shell {id, name, code} when V2 provides those fields.
 * Downstream enrichers detect the shell (`addresses === undefined`) and replace
 * it with the full record from `["partners","global"]` when available; otherwise
 * the shell still gives consumers a usable name. `useCreateManualPreOrder` and
 * `enrichRecapPreOrders` both handle this path explicitly.
 */
export function adaptV2PreOrder(
  row: Record<string, unknown>,
): Record<string, unknown> {
  const partnerId = row.partnerId as string | null | undefined;
  const partnerName = row.partnerName as string | null | undefined;
  const partnerCode = row.partnerCode as string | null | undefined;
  const partnerShell =
    partnerId && (partnerName || partnerCode)
      ? { id: partnerId, name: partnerName ?? null, code: partnerCode ?? null }
      : null;

  // Junction-derived per-chat / per-email message id sets. Surfaced as the
  // legacy `messages` / `sms_messages` / `emails` shape (`{id}` only) so
  // `getWhatsAppMessageIds` / `getSMSMessageIds` keep working and dividers
  // anchor to actually-rendered messages even after group fan-out.
  const whatsappMessageIds = Array.isArray(row.whatsappMessageIds)
    ? (row.whatsappMessageIds as string[])
    : [];
  const smsMessageIds = Array.isArray(row.smsMessageIds)
    ? (row.smsMessageIds as string[])
    : [];
  const emailIds = Array.isArray(row.emailIds)
    ? (row.emailIds as string[])
    : [];

  // Linked WhatsApp/SMS messages get a highlight border driven by
  // `pre_order_metadata.message_ids`. New pre-orders only carry junction rows,
  // so derive the array here (emails excluded — message_ids is for
  // in-conversation messages only). Existing metadata ids are unioned, not
  // dropped.
  const preOrderMetadata = mergeMessageIdsIntoMetadata(
    row.preOrderMetadata as PreOrderMetadata | null | undefined,
    [...whatsappMessageIds, ...smsMessageIds],
  );

  return {
    id: row.id,
    user_id: row.userId,
    group_id: row.groupId,
    partner_id: row.partnerId,
    shipping_address_id: row.shippingAddressId,
    location_id: row.locationId,
    buyer_id: row.buyerId,
    employee_id: row.employeeId,
    status: row.status,
    category: row.category,
    source_type: row.sourceType,
    type: row.type,
    pre_order_number: row.preOrderNumber,
    customer_po_number: row.customerPoNumber,
    comment: row.comment,
    delivery_date: row.deliveryDate,
    order_date: row.orderDate,
    pre_order_metadata: preOrderMetadata,
    created_at: row.createdAt,
    updated_at: row.updatedAt,
    deleted_at: row.deletedAt,
    partner: partnerShell,
    flows: Array.isArray(row.flows) ? row.flows.map(adaptV2PreOrderFlow) : [],
    messages: whatsappMessageIds.map((id) => ({ id })),
    sms_messages: smsMessageIds.map((id) => ({ id })),
    emails: emailIds.map((id) => ({ id })),
  };
}

function adaptV2PreOrderFlow(f: Record<string, unknown>) {
  return {
    id: f.id,
    product_id: f.productId,
    product_name: f.productName,
    quantity: f.quantity,
    weight: f.weight,
    unit_id: f.unitId,
    unit_price: f.unitPrice,
    total_price: f.totalPrice,
    status: f.status,
    original_text: f.originalText,
    comment: f.comment,
    position: f.position,
    ai_confidence: f.aiConfidence,
    // Carry the raw JSONB metadata through: the recap hover-to-highlight
    // (`useFlowHighlightRows` reads `flow_metadata.delineation`), AI change
    // badges, and per-row AI telemetry (`flow_metadata.ai`) all depend on it.
    // Dropping it here left the chat-panel's `activePreOrder.flows` without
    // delineation, so hovering an order line highlighted nothing (STK-1299).
    flow_metadata: (f.flowMetadata as Record<string, unknown> | null) ?? null,
    created_at: f.createdAt,
    updated_at: f.updatedAt,
  };
}

/**
 * Typed convenience: cast the adapted record to the legacy PreOrder type.
 * Caller is responsible for partner hydration if needed.
 */
export function adaptV2PreOrderTyped(row: Record<string, unknown>): PreOrder {
  return adaptV2PreOrder(row) as unknown as PreOrder;
}

export type { PreOrder, PreOrderFlow } from "@/app/api/types/preOrder";
